import { useEffect, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  getAICheckerKnownCases,
  getKnownCaseInvoiceTexts,
  getKnownCaseInvoiceFiles,
  getAICheckerEmailById,
  unmarkAICheckerEmailKnown,
  setAICheckerQuantityFlagKnown,
  deleteAICheckerKnownCategory,
  sendIssueToCodeMonkey,
  type AICheckerKnownCases,
  type AICheckerKnownEmail,
  type AICheckerKnownQtyFlag,
  type AICheckerTrace,
} from '../../../../../../services/admin/adminService';
import { BookmarkCheck, ChevronDown, ChevronRight, Eye, RotateCw, Search, Trash2 } from 'lucide-react';
import MonkeyIcon from '../../../../../common/MonkeyIcon';
import { ReviewCardModal } from '../../../AuditReview';
import { MonkeyButton } from '../../shared/MonkeyButton';
import { aiCheckerEmailItem } from '../buckets';
import type { MonkeyState, TFn } from '../../types';

/**
 * "Known to-dos" — a header button (with a live count) that opens a modal listing the known to-dos
 * (acknowledged-but-unfixed items), grouped by the category stored on each manually-marked email /
 * quantity flag. Email to-dos and quantity-audit to-dos are shown as separate sections and can be
 * narrowed with a filter, so they no longer clutter the main feed.
 */
/** Short date for a line item (falls back to the raw string / em-dash). */
function fmtKnownDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

/** Tiny mailbox badge (63 / 64) shown next to an email line item. */
function acctBadge(account: string | null) {
  if (!account) return null;
  const label = account === 'cloud63' ? '63' : account === 'cloud64' ? '64' : account;
  return <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{label}</span>;
}

/**
 * One known-to-do category card. Collapsed it shows the category and its count badge. Expanded it
 * lists the actual line items filed under the category — each row opens the email's full detail card
 * or un-marks just that one item.
 */
// "!ADD!" known to-dos get a code-monkey button that opens a small form (logo / name / color / notes)
// and launches an add-supplier Claude session, passing the linked invoice emails as context. Color, logo
// and notes are optional: when left empty the session derives them itself from the original invoice PDFs
// (attached to the hand-off) per supplierSupport.md "Deriving the Supplier Brief from Invoice Files".
function AddSupplierMonkeyButton({ td, t }: { td: AICheckerKnownCases['todos'][number]; t: TFn }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(td.category.trim().replace(/^!ADD!\s*/i, ''));
  const [color, setColor] = useState('');
  const [notes, setNotes] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [state, setState] = useState<MonkeyState>('idle');
  const [error, setError] = useState('');

  const toBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(((reader.result as string) || '').split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });
  const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'supplier';

  const submit = async () => {
    if (state === 'loading' || !name.trim()) return;
    setState('loading'); setError('');
    try {
      const files: { name: string; dataBase64: string }[] = [];
      if (logo) files.push({ name: `${slug(name)}_logo.png`, dataBase64: await toBase64(logo) });
      const emailLines = td.emails.map((e) => `- ${e.account ?? '?'} | from: ${e.from ?? '-'} | subject: ${e.subject ?? '-'} | date: ${e.date ?? '-'}`).join('\n');

      const fetchable = td.emails
        .filter((e) => e.gmailMessageId && (e.account === 'cloud63' || e.account === 'cloud64'))
        .map((e) => ({ account: e.account as string, gmailMessageId: e.gmailMessageId as string }));

      // Pull real example invoice texts (pdftotext output) AND the original PDF attachments for the
      // linked emails: texts feed the Gemini few-shot, the PDFs enable logo/color derivation in the
      // session. Both deduped + capped server-side, both best-effort.
      let invoiceTextsSection = '';
      let attachedPdfCount = 0;
      if (fetchable.length > 0) {
        const [texts, pdfs] = await Promise.all([
          getKnownCaseInvoiceTexts(fetchable).catch((err) => {
            // Non-fatal: fall back to the email list so the operator can still retrieve the PDFs by hand.
            console.error('Failed to fetch known-case invoice texts', err);
            return [] as Awaited<ReturnType<typeof getKnownCaseInvoiceTexts>>;
          }),
          getKnownCaseInvoiceFiles(fetchable).catch((err) => {
            console.error('Failed to fetch known-case invoice files', err);
            return [] as Awaited<ReturnType<typeof getKnownCaseInvoiceFiles>>;
          }),
        ]);
        if (texts.length > 0) {
          const blocks = texts.map((tx, i) => {
            const meta = td.emails.find((e) => e.gmailMessageId === tx.gmailMessageId);
            const label = meta ? `${meta.account ?? '?'} | ${meta.subject ?? '-'} | ${meta.date ?? '-'}` : tx.gmailMessageId;
            return `### Example ${i + 1} — ${label}\n\n\`\`\`\n${tx.text}\n\`\`\``;
          }).join('\n\n');
          invoiceTextsSection = `\n\n## Example invoice file texts (pdftotext output)\n${texts.length} distinct example(s) from the linked emails. Use these to build the Gemini few-shot input/output and quantity parsing.\n\n${blocks}`;
        }
        pdfs.forEach((f, i) => files.push({ name: `invoice_${i + 1}_${f.filename.replace(/[^a-zA-Z0-9._-]+/g, '_')}`, dataBase64: f.dataBase64 }));
        attachedPdfCount = pdfs.length;
      }

      const logoLine = logo
        ? 'The operator-provided logo PNG is attached in this task folder under files/ — use it as-is.'
        : attachedPdfCount > 0
          ? 'No logo was provided — derive it from the attached invoice PDF(s) per the derivation section (embedded image → render+crop → online), then derive the color and dimensions.'
          : 'No logo was provided and no PDFs could be attached — locate an invoice in Gmail by sender + subject, or find the official logo online, per the derivation section.';
      const pdfLine = attachedPdfCount > 0
        ? `The ORIGINAL invoice PDF(s) are attached in this task folder under files/ (invoice_*.pdf, ${attachedPdfCount} file(s)).`
        : 'No original PDFs could be attached — find them in Gmail by sender + subject if needed.';

      const caseInfo = `@README.md @z_playbooks/roles/addSupplier.md @z_playbooks/supplierSupport.md — add a new supplier.

Supplier name: ${name}
Brand color: ${color || '(not provided — derive the dominant logo color per the derivation section, default #000000)'}
Notes: ${notes || '-'}
Known-case label: ${td.category}

Example invoice emails (also embedded as file texts below):
${emailLines || '- (none linked)'}

${pdfLine}
${logoLine}${invoiceTextsSection}`;
      await sendIssueToCodeMonkey({
        flow: 'add-supplier',
        caseInfo,
        context: { category: td.category, emailIds: td.emails.map((e) => e.id) },
        files,
      });
      setState('done');
      setTimeout(() => { setState('idle'); setOpen(false); }, 1200);
    } catch (err: any) {
      setState('error'); setError(err.message || 'Failed');
      setTimeout(() => setState('idle'), 4000);
    }
  };

  return (
    <>
      <MonkeyButton state={state} onClick={() => setOpen(true)} title={t('adminTesting.review.addSupplierTitle')} />
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className={cn(cardClass, 'w-full max-w-md p-6 space-y-4')} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.review.addSupplierTitle')}</h3>
              <button onClick={() => setOpen(false)} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
            </div>
            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}

            <label className="block">
              <span className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.addSupplierName')}</span>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={cn('mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)} />
            </label>

            <label className="block">
              <span className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.addSupplierColor')}</span>
              <input type="text" value={color} onChange={(e) => setColor(e.target.value)} placeholder={t('adminTesting.review.addSupplierColorAuto')} className={cn('mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 font-mono', primaryText, smallTextSize)} />
            </label>

            <label className="block">
              <span className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.addSupplierLogo')}</span>
              <input type="file" accept="image/png" onChange={(e) => setLogo(e.target.files?.[0] ?? null)} className={cn('mt-1 w-full text-xs', secondaryText)} />
              {logo
                ? <img src={URL.createObjectURL(logo)} alt="logo preview" className="mt-2 h-16 w-16 object-contain rounded border border-gray-200 dark:border-gray-700" />
                : <span className={cn(secondaryText, 'mt-1 block text-[10px]')}>{t('adminTesting.review.addSupplierLogoAuto')}</span>}
            </label>

            <label className="block">
              <span className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.addSupplierNotes')}</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={cn('mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, smallTextSize)} />
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setOpen(false)} className={cn('px-3 py-1.5 rounded-lg text-xs', secondaryText)}>{t('adminTesting.review.addSupplierCancel')}</button>
              <button onClick={submit} disabled={state === 'loading' || !name.trim()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-white dark:text-white bg-backgroundDarker-dark dark:bg-backgroundDarker-dark hover:opacity-85 disabled:opacity-60">
                <MonkeyIcon className={cn('w-3.5 h-3.5', state === 'loading' && 'animate-spin')} />
                {t('adminTesting.review.addSupplierSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function KnownTodoCard({ td, kind, onOpenEmail, onUnmarkEmail, onUnmarkQty, onDeleteTodo, deleting, openingId, t }: {
  td: AICheckerKnownCases['todos'][number];
  kind: 'emails' | 'quantity';
  onOpenEmail: (traceId: string) => void;
  onUnmarkEmail: (email: AICheckerKnownEmail) => void;
  onUnmarkQty: (flag: AICheckerKnownQtyFlag) => void;
  onDeleteTodo: (td: AICheckerKnownCases['todos'][number]) => void;
  deleting: boolean;
  openingId: string | null;
  t: TFn;
}) {
  const [expanded, setExpanded] = useState(false);
  const count = kind === 'emails' ? td.emailCount : td.qtyCount;
  const items = kind === 'emails' ? td.emails : td.qtyFlags;
  const rowCls = 'flex items-center gap-2 rounded-md border border-amber-100 dark:border-amber-900/30 bg-white/70 dark:bg-gray-800/50 px-2 py-1.5';
  const iconBtn = cn('shrink-0 p-1 rounded-md hover:bg-amber-100 dark:hover:bg-amber-900/40', secondaryText);
  const openBtn = (traceId: string) => (
    <button onClick={() => onOpenEmail(traceId)} disabled={openingId === traceId} title={t('adminTesting.review.knownTodosOpenEmail')} className={iconBtn}>
      {openingId === traceId ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
    </button>
  );

  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-white/60 dark:bg-gray-800/40 p-2.5">
      <div className="flex items-center gap-2">
        <button onClick={() => setExpanded((v) => !v)} className="flex-1 min-w-0 flex items-center gap-2 text-left">
          {expanded ? <ChevronDown className={cn('w-4 h-4 shrink-0', secondaryText)} /> : <ChevronRight className={cn('w-4 h-4 shrink-0', secondaryText)} />}
          <span className={cn(primaryText, semiBoldText, 'text-sm truncate')}>{td.category}</span>
          <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 font-semibold tabular-nums text-xs">
            {count}
          </span>
        </button>
        {kind === 'emails' && td.category.trim().toUpperCase().startsWith('!ADD!') && (
          <AddSupplierMonkeyButton td={td} t={t} />
        )}
        {/* Delete the whole to-do once handled/deployed: re-opens every linked email & flag. */}
        <button
          onClick={() => onDeleteTodo(td)}
          disabled={deleting}
          title={t('adminTesting.review.knownTodosDeleteTitle')}
          aria-label={t('adminTesting.review.knownTodosDelete')}
          className={cn(iconBtn, 'hover:text-red-600 dark:hover:text-red-400')}
        >
          {deleting ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      </div>

      {expanded && (
        <div className="mt-2 ml-6 space-y-1.5">
          {items.length === 0 ? (
            <p className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.review.knownTodosLineNone')}</p>
          ) : kind === 'emails' ? (
            td.emails.map((e) => (
              <div key={e.id} className={rowCls}>
                <button onClick={() => onOpenEmail(e.id)} className="min-w-0 flex-1 text-left group">
                  <div className={cn(primaryText, 'text-xs truncate group-hover:underline')}>{e.subject || '—'}</div>
                  <div className={cn(secondaryText, 'text-[11px] truncate')}>{e.from || '—'} · {fmtKnownDate(e.date)}</div>
                </button>
                {acctBadge(e.account)}
                {openBtn(e.id)}
                <button onClick={() => onUnmarkEmail(e)} title={t('adminTesting.review.knownTodosUnmark')} className={cn(iconBtn, 'hover:text-amber-700 dark:hover:text-amber-300')}>
                  <RotateCw className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          ) : (
            td.qtyFlags.map((f) => (
              <div key={`${f.traceId}:${f.articleId}:${f.productName}`} className={rowCls}>
                <button onClick={() => onOpenEmail(f.traceId)} className="min-w-0 flex-1 text-left group">
                  <div className={cn(primaryText, 'text-xs truncate group-hover:underline')}>{f.productName || '—'}</div>
                  <div className={cn(secondaryText, 'text-[11px] truncate')}>{f.restaurantName || '—'}{f.reason ? ` · ${f.reason}` : ''}</div>
                </button>
                {openBtn(f.traceId)}
                <button onClick={() => onUnmarkQty(f)} title={t('adminTesting.review.knownTodosUnmark')} className={cn(iconBtn, 'hover:text-amber-700 dark:hover:text-amber-300')}>
                  <RotateCw className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function AICheckerKnownCasesPanel({ account, startDate, endDate, reloadNonce, onChanged, t }: {
  account: string; startDate: string; endDate: string; reloadNonce: number; onChanged?: () => void; t: TFn;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'emails' | 'quantity'>('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<AICheckerKnownCases | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The email whose full detail card is open (loaded on demand), + which line is currently loading.
  const [detail, setDetail] = useState<AICheckerTrace | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  // Category currently being deleted (whole to-do) — disables its delete button while in flight.
  const [deletingCat, setDeletingCat] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAICheckerKnownCases(account, startDate, endDate)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: any) => { if (!cancelled) setError(e.message || 'Failed to load known cases'); });
    return () => { cancelled = true; };
  }, [account, startDate, endDate, reloadNonce]);

  const openEmail = async (traceId: string) => {
    if (!traceId) return;
    try {
      setError(null);
      setOpeningId(traceId);
      setDetail(await getAICheckerEmailById(traceId));
    } catch (e: any) {
      setError(e.message || 'Failed to open email');
    } finally {
      setOpeningId(null);
    }
  };

  const unmarkEmail = async (email: AICheckerKnownEmail) => {
    try {
      setError(null);
      await unmarkAICheckerEmailKnown(email.id);
      onChanged?.();
    } catch (e: any) {
      setError(e.message || 'Failed to un-mark email');
    }
  };

  const unmarkQty = async (flag: AICheckerKnownQtyFlag) => {
    try {
      setError(null);
      await setAICheckerQuantityFlagKnown(flag.traceId, flag.articleId, flag.productName, '', 'pakbon', false);
      onChanged?.();
    } catch (e: any) {
      setError(e.message || 'Failed to un-mark flag');
    }
  };

  const deleteTodo = async (td: AICheckerKnownCases['todos'][number]) => {
    if (!window.confirm(t('adminTesting.review.knownTodosDeleteConfirm', { category: td.category }))) return;
    try {
      setError(null);
      setDeletingCat(td.category);
      await deleteAICheckerKnownCategory(td.category);
      onChanged?.(); // re-fetch: the to-do disappears and its emails/flags re-open as issues
    } catch (e: any) {
      setError(e.message || 'Failed to delete to-do');
    } finally {
      setDeletingCat(null);
    }
  };

  // Nothing configured and nothing matched → don't surface the button at all.
  if (!data || (data.todos.length === 0 && data.total === 0)) return null;

  // Search filters whole cards by category name or any linked line item's text.
  const q = search.trim().toLowerCase();
  const matchesSearch = (td: AICheckerKnownCases['todos'][number]) => {
    if (!q) return true;
    const hay = [
      td.category,
      ...td.emails.map((e) => `${e.subject ?? ''} ${e.from ?? ''}`),
      ...td.qtyFlags.map((f) => `${f.productName} ${f.restaurantName} ${f.reason}`),
    ].join(' ').toLowerCase();
    return hay.includes(q);
  };
  const todos = data.todos.filter(matchesSearch);
  const emailTodos = todos.filter((td) => td.emailCount > 0);
  const qtyTodos = todos.filter((td) => td.qtyCount > 0);
  const emailTotal = emailTodos.reduce((n, td) => n + td.emailCount, 0);
  const qtyTotal = qtyTodos.reduce((n, td) => n + td.qtyCount, 0);

  const showEmails = filter === 'all' || filter === 'emails';
  const showQty = filter === 'all' || filter === 'quantity';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={t('adminTesting.review.knownTodosDesc')}
        className={cn('inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-800 dark:text-amber-200 whitespace-nowrap', smallTextSize, semiBoldText)}
      >
        <BookmarkCheck className="w-4 h-4 shrink-0" />
        {t('adminTesting.review.knownTodosButtonLabel')}
        <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/50 text-amber-900 dark:text-amber-100 text-xs tabular-nums">
          {data.todos.length}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className={cn(cardClass, 'w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto')} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className={cn(primaryText, semiBoldText, 'flex items-center gap-1.5')}>
                  <BookmarkCheck className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  {t('adminTesting.review.knownTodosHeading', { count: data.total })}
                </h3>
                <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.review.knownTodosDesc')}</p>
              </div>
              <button onClick={() => setOpen(false)} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
            </div>

            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}

            <div className="flex flex-wrap items-center gap-2">
              {/* Filter: All / Emails / Quantity */}
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                {([
                  ['all', t('adminTesting.review.knownTodosFilterAll')],
                  ['emails', t('adminTesting.review.knownTodosFilterEmails')],
                  ['quantity', t('adminTesting.review.knownTodosFilterQty')],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={cn('px-3 py-1.5 transition-colors', smallTextSize, semiBoldText,
                      filter === key ? 'bg-purple-600 dark:bg-purple-600 text-white dark:text-white' : cn('bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600', primaryText))}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[140px]">
                <Search className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5', secondaryText)} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('adminTesting.review.knownTodosSearchPlaceholder')}
                  className={cn('w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, 'text-xs')}
                />
              </div>
            </div>

            {data.todos.length === 0 ? (
              <p className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.review.knownTodosNone')}</p>
            ) : todos.length === 0 ? (
              <p className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.review.knownTodosNoMatch')}</p>
            ) : (
              <div className="space-y-5">
                {showEmails && (
                  <div>
                    <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2')}>
                      {t('adminTesting.review.knownTodosEmailsHeading', { count: emailTotal })}
                    </div>
                    {emailTodos.length === 0 ? (
                      <p className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.review.knownTodosEmailsNone')}</p>
                    ) : (
                      <div className="space-y-2.5">{emailTodos.map((td) => (
                        <KnownTodoCard key={`e:${td.category}`} td={td} kind="emails" onOpenEmail={openEmail} onUnmarkEmail={unmarkEmail} onUnmarkQty={unmarkQty} onDeleteTodo={deleteTodo} deleting={deletingCat === td.category} openingId={openingId} t={t} />
                      ))}</div>
                    )}
                  </div>
                )}
                {showQty && (
                  <div>
                    <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2')}>
                      {t('adminTesting.review.knownTodosQtyHeading', { count: qtyTotal })}
                    </div>
                    {qtyTodos.length === 0 ? (
                      <p className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.review.knownTodosQtyNone')}</p>
                    ) : (
                      <div className="space-y-2.5">{qtyTodos.map((td) => (
                        <KnownTodoCard key={`q:${td.category}`} td={td} kind="quantity" onOpenEmail={openEmail} onUnmarkEmail={unmarkEmail} onUnmarkQty={unmarkQty} onDeleteTodo={deleteTodo} deleting={deletingCat === td.category} openingId={openingId} t={t} />
                      ))}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {detail && (
            <ReviewCardModal
              title={detail.emailSubject || detail.emailFrom || '—'}
              item={aiCheckerEmailItem(detail, t, () => { setDetail(null); onChanged?.(); })}
              onClose={() => setDetail(null)}
              t={t}
            />
          )}
        </div>
      )}
    </>
  );
}
